/*
 * Pure derivation helpers for the season ranking lists (/convention-rankings).
 * Input rows come from GET /api/conventions/rankings — one row per start:
 * { participant, name, club, convention, nsfNumber, title, date, stageMd5,
 *   lagIdx, stageType, weaponClass, poeng, innertreff, hits, shots, standsScored,
 *   gender ('k' | 'm' | ''), birthYear }
 */

import { getStageTypeDisplay, resolveWeaponDisplay } from './conventionResults';

const BEST_COUNT = 3;
// How many of the biggest disciplines get their own line in the trend charts.
const TREND_DISCIPLINES = 4;

export function disciplineKeyOf(row) {
    return `${row.stageType}::${row.weaponClass}`;
}

export function formatDisciplineLabel(stageType, weaponClass) {
    const weapon = resolveWeaponDisplay(weaponClass);
    if (stageType === 'norgesfelt' || stageType === 'felt') {
        return weapon;
    }
    return `${getStageTypeDisplay(stageType)} · ${weapon}`;
}

export function formatRankingDate(date) {
    if (!date) {
        return '';
    }
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) {
        return '';
    }
    return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;
}

function compareResults(a, b) {
    if (b.poeng !== a.poeng) {
        return b.poeng - a.poeng;
    }
    return b.innertreff - a.innertreff;
}

// Dense ranking with shared places: equal poeng + innertreff share plass,
// the next distinct result gets plass + 1 (Norgesfelt style).
export function rankWithSharedPlaces(items) {
    const sorted = [...items].sort((a, b) => compareResults(a, b) || String(a.name).localeCompare(String(b.name), 'nb'));
    let plass = 0;
    let prev = null;
    return sorted.map((item) => {
        if (!prev || compareResults(prev, item) !== 0) {
            plass += 1;
        }
        prev = item;
        return { ...item, plass };
    });
}

export function groupDisciplines(rows) {
    const byKey = {};
    (rows || []).forEach((row) => {
        const key = disciplineKeyOf(row);
        if (!byKey[key]) {
            byKey[key] = {
                key,
                stageType: row.stageType,
                weaponClass: row.weaponClass,
                label: formatDisciplineLabel(row.stageType, row.weaponClass),
                rowCount: 0,
                shooters: new Set(),
            };
        }
        byKey[key].rowCount += 1;
        byKey[key].shooters.add(row.participant);
    });
    return Object.values(byKey)
        .map(d => ({
            key: d.key,
            stageType: d.stageType,
            weaponClass: d.weaponClass,
            label: d.label,
            rowCount: d.rowCount,
            shooterCount: d.shooters.size,
        }))
        .sort((a, b) => b.rowCount - a.rowCount || a.label.localeCompare(b.label, 'nb'));
}

function rowsForDiscipline(rows, disciplineKey) {
    return (rows || []).filter(row => disciplineKeyOf(row) === disciplineKey);
}

function groupByShooter(rows) {
    const byShooter = {};
    rows.forEach((row) => {
        if (!byShooter[row.participant]) {
            byShooter[row.participant] = [];
        }
        byShooter[row.participant].push(row);
    });
    return byShooter;
}

export function bestSingleList(rows, disciplineKey) {
    const byShooter = groupByShooter(rowsForDiscipline(rows, disciplineKey));
    const items = Object.values(byShooter).map((shooterRows) => {
        const sorted = [...shooterRows].sort(compareResults);
        const best = sorted[0];
        return {
            participant: best.participant,
            name: best.name,
            club: best.club,
            poeng: best.poeng,
            innertreff: best.innertreff,
            date: best.date,
            nsfNumber: best.nsfNumber,
            convention: best.convention,
            resultCount: shooterRows.length,
        };
    });
    return rankWithSharedPlaces(items);
}

export function bestThreeList(rows, disciplineKey) {
    const byShooter = groupByShooter(rowsForDiscipline(rows, disciplineKey));
    const items = Object.values(byShooter).map((shooterRows) => {
        const sorted = [...shooterRows].sort(compareResults);
        const counted = sorted.slice(0, BEST_COUNT);
        return {
            participant: counted[0].participant,
            name: counted[0].name,
            club: counted[0].club,
            poeng: counted.reduce((sum, r) => sum + r.poeng, 0),
            innertreff: counted.reduce((sum, r) => sum + r.innertreff, 0),
            resultCount: shooterRows.length,
            countedCount: counted.length,
        };
    });
    return rankWithSharedPlaces(items);
}

// Best poeng per convention within one discipline — the 100 % reference for
// «% av beste», same rule as the stevne result list (winner of the weapon class
// that day, not full house).
function bestPoengByConvention(disciplineRows) {
    const best = {};
    disciplineRows.forEach((row) => {
        if (!(best[row.convention] > row.poeng)) {
            best[row.convention] = row.poeng;
        }
    });
    return best;
}

export function shooterSeasonDetail(rows, disciplineKey, participant) {
    const disciplineRows = rowsForDiscipline(rows, disciplineKey);
    const shooterRows = disciplineRows.filter(row => row.participant === participant);
    if (shooterRows.length === 0) {
        return null;
    }
    const bestByConvention = bestPoengByConvention(disciplineRows);
    const sorted = [...shooterRows].sort((a, b) => compareResults(a, b) || new Date(b.date) - new Date(a.date));
    const results = sorted.map((row, idx) => {
        const best = bestByConvention[row.convention];
        const percent = best > 0 ? Math.round((row.poeng / best) * 100) : null;
        return { ...row, counted: idx < BEST_COUNT, percent };
    });
    const counted = results.filter(r => r.counted);
    const withPercent = results.filter(r => r.percent !== null);
    return {
        participant,
        name: sorted[0].name,
        club: sorted[0].club,
        results,
        totalPoeng: counted.reduce((sum, r) => sum + r.poeng, 0),
        totalInnertreff: counted.reduce((sum, r) => sum + r.innertreff, 0),
        averagePercent: withPercent.length > 0
            ? Math.round(withPercent.reduce((sum, r) => sum + r.percent, 0) / withPercent.length)
            : null,
    };
}

// ---------------------------------------------------------------------------
// Season summary (the stats block above the ranking lists).
// ---------------------------------------------------------------------------

export const MONTH_SHORT = ['jan', 'feb', 'mar', 'apr', 'mai', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'des'];

// NSF age classes by the year the shooter turns N during the season.
export const AGE_GROUPS = [
    { key: 'junior', label: 'Junior', max: 20 },
    { key: 'senior', label: 'Senior', max: 49 },
    { key: 'v50', label: 'V50', max: 59 },
    { key: 'v60', label: 'V60', max: 69 },
    { key: 'v70', label: 'V70', max: Infinity },
];

export function ageGroupKey(birthYear, seasonYear) {
    if (!birthYear || !seasonYear) {
        return 'unknown';
    }
    const age = seasonYear - birthYear;
    if (age < 0 || age > 120) {
        return 'unknown';
    }
    const group = AGE_GROUPS.find(g => age <= g.max);
    return group ? group.key : 'unknown';
}

function round1(value) {
    return Math.round(value * 10) / 10;
}

function maxBy(items, score) {
    let best = null;
    let bestScore = -Infinity;
    (items || []).forEach((item) => {
        const s = score(item);
        if (s > bestScore) {
            best = item;
            bestScore = s;
        }
    });
    return best;
}

/**
 * Aggregates the per-start ranking rows for one season into the numbers and
 * chart series the summary block renders. Pure — no store access.
 * Shooter-level buckets (gender, age) count each shooter once; a shooter's
 * gender/birthYear is taken from the first row that carries a value.
 */
export function buildSeasonStats(rows, seasonYear) {
    const list = rows || [];
    const shooters = {};
    const clubs = new Set();
    const clubStats = {};
    const conventions = {};
    const months = MONTH_SHORT.map(label => ({ label, value: 0 }));
    const monthHits = MONTH_SHORT.map(() => ({ hits: 0, shots: 0 }));
    const disciplines = {};
    const standTotals = {};
    const histogram = {};
    let perfectCount = 0;
    let shots = 0;
    let hits = 0;
    let innertreff = 0;
    let poeng = 0;

    list.forEach((row) => {
        const shooter = shooters[row.participant] || {
            participant: row.participant,
            name: row.name,
            club: row.club,
            starts: 0,
            gender: '',
            birthYear: 0,
            conventions: new Set(),
        };
        shooter.starts += 1;
        shooter.conventions.add(row.convention);
        if (!shooter.gender && row.gender) shooter.gender = row.gender;
        if (!shooter.birthYear && row.birthYear) shooter.birthYear = row.birthYear;
        shooters[row.participant] = shooter;

        if (row.club) clubs.add(row.club);
        const clubKey = row.club || '';
        const club = clubStats[clubKey] || {
            club: clubKey,
            starts: 0,
            poeng: 0,
            shooters: new Set(),
            best: null,
        };
        club.starts += 1;
        club.poeng += row.poeng || 0;
        club.shooters.add(row.participant);
        if (!club.best || (row.poeng || 0) * 1000 + (row.innertreff || 0) > club.best.poeng * 1000 + club.best.innertreff) {
            club.best = {
                participant: row.participant,
                name: row.name,
                poeng: row.poeng || 0,
                innertreff: row.innertreff || 0,
                disciplineKey: disciplineKeyOf(row),
                disciplineLabel: formatDisciplineLabel(row.stageType, row.weaponClass),
            };
        }
        clubStats[clubKey] = club;

        const conv = conventions[row.convention] || {
            convention: row.convention,
            title: row.title,
            nsfNumber: row.nsfNumber,
            date: row.date,
            starts: 0,
            shooters: new Set(),
        };
        conv.starts += 1;
        conv.shooters.add(row.participant);
        conventions[row.convention] = conv;

        const d = row.date ? new Date(row.date) : null;
        if (d && !Number.isNaN(d.getTime())) {
            months[d.getMonth()].value += 1;
        }

        const key = disciplineKeyOf(row);
        if (!disciplines[key]) {
            disciplines[key] = {
                key,
                label: formatDisciplineLabel(row.stageType, row.weaponClass),
                value: 0,
                byMonth: MONTH_SHORT.map(() => 0),
                monthHits: MONTH_SHORT.map(() => ({ hits: 0, shots: 0 })),
                poeng: 0,
                hits: 0,
                shots: 0,
                perfect: 0,
                stands: {},
            };
        }
        disciplines[key].value += 1;
        if (d && !Number.isNaN(d.getTime())) {
            disciplines[key].byMonth[d.getMonth()] += 1;
        }

        shots += row.shots || 0;
        hits += row.hits || 0;
        innertreff += row.innertreff || 0;
        poeng += row.poeng || 0;

        // Discipline-level sums for averages, hit % and perfect starts.
        const dsc = disciplines[key];
        dsc.poeng += row.poeng || 0;
        dsc.hits += row.hits || 0;
        dsc.shots += row.shots || 0;
        if ((row.shots || 0) > 0 && row.hits === row.shots) {
            dsc.perfect += 1;
            perfectCount += 1;
        }
        if (d && !Number.isNaN(d.getTime())) {
            const mi = d.getMonth();
            monthHits[mi].hits += row.hits || 0;
            monthHits[mi].shots += row.shots || 0;
            dsc.monthHits[mi].hits += row.hits || 0;
            dsc.monthHits[mi].shots += row.shots || 0;
        }

        // Per-stand hits (backend rows carry stands: [{ stand, hits, inner, shots }]).
        (row.stands || []).forEach((st) => {
            if (!(st.stand > 0)) return;
            const tot = standTotals[st.stand] || { stand: st.stand, hits: 0, shots: 0 };
            tot.hits += st.hits || 0;
            tot.shots += st.shots || 0;
            standTotals[st.stand] = tot;
            const dst = dsc.stands[st.stand] || { stand: st.stand, hits: 0, shots: 0 };
            dst.hits += st.hits || 0;
            dst.shots += st.shots || 0;
            dsc.stands[st.stand] = dst;
        });

        // Poeng histogram, buckets of 10.
        const bucket = Math.max(0, Math.floor((row.poeng || 0) / 10));
        histogram[bucket] = (histogram[bucket] || 0) + 1;
    });

    const shooterList = Object.values(shooters);
    const gender = { k: 0, m: 0, unknown: 0 };
    const ageCounts = { unknown: 0 };
    AGE_GROUPS.forEach((g) => { ageCounts[g.key] = 0; });
    shooterList.forEach((s) => {
        if (s.gender === 'k' || s.gender === 'm') gender[s.gender] += 1;
        else gender.unknown += 1;
        ageCounts[ageGroupKey(s.birthYear, seasonYear)] += 1;
    });

    const conventionList = Object.values(conventions).map(c => ({
        convention: c.convention,
        title: c.title,
        nsfNumber: c.nsfNumber,
        date: c.date,
        starts: c.starts,
        shooterCount: c.shooters.size,
    }));

    const disciplineList = Object.values(disciplines)
        .sort((a, b) => b.value - a.value || a.label.localeCompare(b.label, 'nb'));
    // Same order as startsPerDiscipline so a caller can colour both charts alike.
    const startsPerMonthByDiscipline = months.map((m, idx) => ({
        label: m.label,
        value: m.value,
        segments: disciplineList.map(dsc => ({ key: dsc.key, label: dsc.label, value: dsc.byMonth[idx] })),
    }));

    // Per club, most starts first; shooters without a club are grouped under ''.
    const clubList = Object.values(clubStats)
        .map(c => ({
            club: c.club,
            starts: c.starts,
            shooterCount: c.shooters.size,
            avgStartsPerShooter: c.shooters.size > 0 ? round1(c.starts / c.shooters.size) : null,
            avgPoeng: c.starts > 0 ? round1(c.poeng / c.starts) : null,
            best: c.best,
        }))
        .sort((a, b) => b.starts - a.starts || a.club.localeCompare(b.club, 'nb'));

    const pctOf = (h, sh) => (sh > 0 ? round1((h / sh) * 100) : null);
    const topDisciplines = disciplineList.slice(0, TREND_DISCIPLINES);

    // Hit % per month, total and for the biggest disciplines.
    const hitPctPerMonth = MONTH_SHORT.map((label, idx) => ({
        label,
        pct: pctOf(monthHits[idx].hits, monthHits[idx].shots),
        byDiscipline: topDisciplines.map(dsc => ({
            key: dsc.key,
            label: dsc.label,
            pct: pctOf(dsc.monthHits[idx].hits, dsc.monthHits[idx].shots),
        })),
    }));

    // Season conventions in date order; a shooter is "new" at their first one.
    const conventionsByDate = [...conventionList].sort((a, b) => new Date(a.date) - new Date(b.date) || a.convention - b.convention);
    const conventionOrder = {};
    conventionsByDate.forEach((c, idx) => { conventionOrder[c.convention] = idx; });
    const firstConventionOf = {};
    shooterList.forEach((sh) => {
        const idxs = [...sh.conventions].map(c => conventionOrder[c]).filter(i => i != null);
        firstConventionOf[sh.participant] = idxs.length > 0 ? Math.min(...idxs) : null;
    });
    const participantsPerConvention = conventionsByDate.map((c, idx) => {
        const newCount = shooterList.filter(sh => firstConventionOf[sh.participant] === idx).length;
        return {
            ...c,
            newCount,
            returningCount: c.shooterCount - newCount,
        };
    });

    // New shooters per month (month of their first start in the season).
    const newShootersPerMonth = MONTH_SHORT.map(label => ({ label, value: 0 }));
    shooterList.forEach((sh) => {
        const first = conventionsByDate[firstConventionOf[sh.participant]];
        const fd = first && first.date ? new Date(first.date) : null;
        if (fd && !Number.isNaN(fd.getTime())) {
            newShootersPerMonth[fd.getMonth()].value += 1;
        }
    });

    // Longest run of consecutive season conventions attended.
    const streaks = shooterList.map((sh) => {
        const attended = new Set([...sh.conventions].map(c => conventionOrder[c]));
        let best = 0;
        let run = 0;
        for (let i = 0; i < conventionsByDate.length; i += 1) {
            run = attended.has(i) ? run + 1 : 0;
            if (run > best) best = run;
        }
        return {
            participant: sh.participant, name: sh.name, club: sh.club, streak: best,
        };
    }).sort((a, b) => b.streak - a.streak || a.name.localeCompare(b.name, 'nb'));

    // Poeng distribution in buckets of 10 up to the highest bucket seen.
    const maxBucket = Object.keys(histogram).length > 0 ? Math.max(...Object.keys(histogram).map(Number)) : 0;
    const poengHistogram = [];
    for (let b = 0; b <= maxBucket; b += 1) {
        poengHistogram.push({ label: `${b * 10}–${b * 10 + 9}`, from: b * 10, to: b * 10 + 9, value: histogram[b] || 0 });
    }

    // Hit % per stand (1..n), total and for the biggest disciplines.
    const standNumbers = Object.keys(standTotals).map(Number).sort((a, b) => a - b);
    const standHitPct = {
        total: standNumbers.map(n => ({
            stand: n, hits: standTotals[n].hits, shots: standTotals[n].shots, pct: pctOf(standTotals[n].hits, standTotals[n].shots),
        })),
        byDiscipline: topDisciplines.map(dsc => ({
            key: dsc.key,
            label: dsc.label,
            stands: standNumbers.map(n => ({
                stand: n,
                pct: dsc.stands[n] ? pctOf(dsc.stands[n].hits, dsc.stands[n].shots) : null,
            })),
        })),
    };

    const perDiscipline = disciplineList.map(dsc => ({
        key: dsc.key,
        label: dsc.label,
        starts: dsc.value,
        avgPoeng: dsc.value > 0 ? round1(dsc.poeng / dsc.value) : null,
        hitPct: pctOf(dsc.hits, dsc.shots),
        perfectCount: dsc.perfect,
    }));

    const mostActive = maxBy(shooterList, s => s.starts);
    const biggestConvention = maxBy(conventionList, c => c.starts);
    const bestRow = maxBy(list, r => (r.poeng || 0) * 1000 + (r.innertreff || 0));

    return {
        startCount: list.length,
        shooterCount: shooterList.length,
        clubCount: clubs.size,
        conventionCount: conventionList.length,
        shots,
        hits,
        hitPct: shots > 0 ? round1((hits / shots) * 100) : null,
        innertreff,
        innerPct: hits > 0 ? round1((innertreff / hits) * 100) : null,
        avgPoeng: list.length > 0 ? round1(poeng / list.length) : null,
        avgStartsPerShooter: shooterList.length > 0 ? round1(list.length / shooterList.length) : null,
        gender,
        ageGroups: [
            ...AGE_GROUPS.map(g => ({ key: g.key, label: g.label, count: ageCounts[g.key] })),
            { key: 'unknown', label: '', count: ageCounts.unknown },
        ],
        clubs: clubList,
        perfectCount,
        hitPctPerMonth,
        participantsPerConvention,
        newShootersPerMonth,
        longestStreaks: streaks.slice(0, 3).filter(x => x.streak > 0),
        poengHistogram,
        standHitPct,
        perDiscipline,
        startsPerMonth: months,
        startsPerMonthByDiscipline,
        startsPerDiscipline: disciplineList.map(dsc => ({ key: dsc.key, label: dsc.label, value: dsc.value })),
        mostActiveShooter: mostActive
            ? {
                participant: mostActive.participant, name: mostActive.name, club: mostActive.club, starts: mostActive.starts,
            }
            : null,
        biggestConvention,
        bestResult: bestRow
            ? {
                participant: bestRow.participant,
                name: bestRow.name,
                club: bestRow.club,
                poeng: bestRow.poeng,
                innertreff: bestRow.innertreff,
                date: bestRow.date,
                nsfNumber: bestRow.nsfNumber,
                disciplineKey: disciplineKeyOf(bestRow),
                disciplineLabel: formatDisciplineLabel(bestRow.stageType, bestRow.weaponClass),
            }
            : null,
    };
}
